import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-248 — Agent V2 settings as a resizeable right sidebar.
//
// Replaces the inline drawer above the chat (the previous shape choked
// the chat surface when the settings sections expanded — system prompt
// alone is now ~7K chars). The sidebar lives to the right of the chat,
// has a vertical drag handle on its left edge, and persists its width
// in localStorage so the user's preferred sizing sticks across reloads.
//
// Section order (per Akari):
//   1. Active model (always rendered open — primary control)
//   2. System prompt   ┐
//   3. MCP servers     ├ collapsed by default
//   4. Channel subs    │
//   5. Danger zone     ┘
//
// Each section uses a SettingsSection helper that handles the collapsed
// chevron + click-to-toggle. The "Active model" surface skips the
// header so it reads as the primary content of the sidebar.
import { useCallback, useEffect, useRef, useState, } from "react";
import { ChannelSubscriptionsEditor } from "./ChannelSubscriptionsEditor.js";
import { DangerZone } from "./DangerZone.js";
import { Icon } from "./icons.js";
import { McpServerEditor } from "./McpServerEditor.js";
import { ModelPicker } from "./ModelPicker.js";
import { SystemPromptEditor } from "./SystemPromptEditor.js";
const WIDTH_STORAGE_KEY = "kritaka:agent-v2:settings-sidebar-width";
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;
function readStoredWidth() {
    try {
        const v = parseInt(localStorage.getItem(WIDTH_STORAGE_KEY) ?? "", 10);
        if (Number.isFinite(v) && v >= MIN_WIDTH && v <= MAX_WIDTH)
            return v;
    }
    catch {
        /* localStorage unavailable in some sandboxed contexts */
    }
    return DEFAULT_WIDTH;
}
export function SettingsSidebar({ agentName, activeModel, systemPrompt, onSaveSystemPrompt, onSetModel, channelOptions, subscribedChannelIds, dmChannelId, onToggleSubscription, mcpConfig, onSaveMcpConfig, onDelete, onClose, }) {
    const [width, setWidth] = useState(readStoredWidth);
    const dragStartRef = useRef(null);
    // Drag-to-resize. Handle is the left edge of the sidebar; dragging
    // left increases width, right shrinks. Width clamped to MIN/MAX.
    const onDragStart = useCallback((e) => {
        e.preventDefault();
        dragStartRef.current = { startX: e.clientX, startWidth: width };
    }, [width]);
    useEffect(() => {
        function onMove(e) {
            if (!dragStartRef.current)
                return;
            const dx = dragStartRef.current.startX - e.clientX; // dragging left grows
            const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartRef.current.startWidth + dx));
            setWidth(next);
        }
        function onUp() {
            if (dragStartRef.current) {
                try {
                    localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
                }
                catch {
                    /* ignore */
                }
            }
            dragStartRef.current = null;
        }
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
        return () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
    }, [width]);
    return (_jsxs("aside", { className: "drawer", style: { width }, children: [_jsx("div", { onMouseDown: onDragStart, style: {
                    position: "absolute",
                    left: -3,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    cursor: "col-resize",
                    zIndex: 2,
                }, title: "Drag to resize" }), _jsxs("div", { className: "drawer__head", children: [_jsx("span", { children: "Agent settings" }), _jsx("button", { type: "button", className: "drawer__close", onClick: onClose, "aria-label": "Close settings", children: _jsx(Icon.x, {}) })] }), _jsxs("div", { className: "drawer__body", children: [onSetModel && (_jsx("div", { style: {
                            padding: "12px 14px 14px",
                            borderBottom: "1px solid var(--hairline)",
                            background: "var(--paper)",
                        }, children: _jsx(ModelPicker, { activeModel: activeModel, onSave: onSetModel }) })), _jsx(SettingsSection, { title: "System prompt", defaultOpen: false, children: _jsx("div", { style: { padding: "0 14px 14px" }, children: _jsx(SystemPromptEditor, { activeModel: activeModel, initial: systemPrompt, onSave: onSaveSystemPrompt, onClose: () => undefined }) }) }), mcpConfig && onSaveMcpConfig && (_jsx(SettingsSection, { title: "MCP servers", defaultOpen: false, children: _jsx("div", { style: { padding: "0 14px 14px" }, children: _jsx(McpServerEditor, { initial: mcpConfig, onSave: onSaveMcpConfig }) }) })), channelOptions && subscribedChannelIds && onToggleSubscription && (_jsx(SettingsSection, { title: "Channel subscriptions", defaultOpen: false, children: _jsx("div", { style: { padding: "0 14px 14px" }, children: _jsx(ChannelSubscriptionsEditor, { channels: channelOptions, subscribedIds: subscribedChannelIds, dmChannelId: dmChannelId, onToggle: onToggleSubscription }) }) })), onDelete && (_jsx(SettingsSection, { title: "Danger zone", defaultOpen: false, variant: "danger", children: _jsx("div", { style: { padding: "0 14px 14px" }, children: _jsx(DangerZone, { agentName: agentName, onDelete: onDelete }) }) }))] })] }));
}
// KTK-249 — collapsible section using the prototype's .sec class
// pattern. The chev rotates via CSS keyed off `data-open`.
function SettingsSection({ title, defaultOpen = false, children, variant = "default" }) {
    const [open, setOpen] = useState(defaultOpen);
    return (_jsxs("div", { className: "sec", "data-open": open ? "true" : "false", children: [_jsxs("div", { role: "button", tabIndex: 0, className: variant === "danger" ? "sec__head sec__head--danger" : "sec__head", onClick: () => setOpen((o) => !o), onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen((o) => !o);
                    }
                }, children: [_jsx("span", { className: "sec__chev", children: _jsx(Icon.chev, {}) }), _jsx("span", { className: "sec__label", children: title })] }), open && _jsx("div", { className: "sec__body", children: children })] }));
}
//# sourceMappingURL=SettingsSidebar.js.map